import { useQuery } from '@tanstack/react-query';
import { leadsService } from '../../../services/leads.service';
import { useProduct } from '../../../contexts/ProductContext';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import { DocugenLifecycleBadge } from '../../../components/crm/DocugenLifecycleBadge';
import { Badge, EmptyState } from '../../../ui';
import { formatDateBR } from '../../../utils/format';

interface Props {
  filter?: 'all' | 'due';
  onLeadClick: (id: number) => void;
}

interface LeadRow {
  id: number;
  name: string;
  company: string | null;
  stage?: { name: string; type?: string | null } | null;
  nextFollowUpAt: string | null;
  dealValue: string | number | null;
  integrationId?: number | null;
  externalCreatedAt?: string | null;
  externalDocsGenerated?: number | null;
  externalDocsUsed?: number | null;
  externalDocsLimit?: number | null;
  externalFirstDocAt?: string | null;
  externalLastActivityAt?: string | null;
  externalTriedLockedFeature?: boolean | null;
  externalTotalTokens?: number | null;
}

function formatMoney(value: string | number | null): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  const n = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(n) || n === 0) {
    return '—';
  }
  return `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export function PipelineList({ filter = 'all', onLeadClick }: Props) {
  const { selectedProductId } = useProduct();

  const { data, isLoading } = useQuery({
    queryKey: ['pipeline-list', selectedProductId, filter],
    queryFn: async () => {
      const result = await leadsService.list({ productId: selectedProductId, limit: 200 });
      return (result?.leads ?? result?.items ?? result ?? []) as LeadRow[];
    },
    enabled: !!selectedProductId,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const now = new Date();
  const list = data ?? [];
  const filtered =
    filter === 'due'
      ? list.filter((l) => l.nextFollowUpAt && new Date(l.nextFollowUpAt) < now)
      : list;

  if (filtered.length === 0) {
    return (
      <EmptyState
        title="Sem leads"
        body={filter === 'due' ? 'Você está em dia com follow-ups.' : 'Crie um lead para começar.'}
      />
    );
  }

  return (
    <div className="bg-surface rounded-lg border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-subtle">
              <th className="text-left px-4 py-3 font-medium text-ink-secondary">Nome</th>
              <th className="text-left px-4 py-3 font-medium text-ink-secondary">Empresa</th>
              <th className="text-left px-4 py-3 font-medium text-ink-secondary">Stage</th>
              <th className="text-left px-4 py-3 font-medium text-ink-secondary">Categoria</th>
              <th className="text-left px-4 py-3 font-medium text-ink-secondary">
                Próximo follow-up
              </th>
              <th className="text-left px-4 py-3 font-medium text-ink-secondary">Valor</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map((l) => {
              const overdue = l.nextFollowUpAt && new Date(l.nextFollowUpAt) < now;
              return (
                <tr
                  key={l.id}
                  onClick={() => onLeadClick(l.id)}
                  className="hover:bg-subtle cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 text-ink-primary font-medium">{l.name}</td>
                  <td className="px-4 py-3 text-ink-secondary">{l.company ?? '—'}</td>
                  <td className="px-4 py-3">
                    <Badge tone="neutral">{l.stage?.name ?? '—'}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    <DocugenLifecycleBadge
                      lead={{ ...l, stageType: l.stage?.type }}
                      variant="compact"
                    />
                  </td>
                  <td
                    className={`px-4 py-3 ${overdue ? 'text-danger font-medium' : 'text-ink-secondary'}`}
                  >
                    {formatDateBR(l.nextFollowUpAt)}
                  </td>
                  <td className="px-4 py-3 text-ink-secondary font-mono">
                    {formatMoney(l.dealValue)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
